// PasskeyBanner — "Ein-Tap-Login einrichten" (AUFTRAG E P1).
//
// Zeigt sich genau dann, wenn: volle Session + Browser kann WebAuthn +
// noch KEIN Passkey registriert + heute nicht weggeklickt. Ein Klick
// registriert einen Passkey für dieses Gerät (Label editierbar) — danach
// loggt Kevin sich 30 Tage lang mit Windows Hello / Face ID / YubiKey ein.

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/auth";
import { UnauthorizedError } from "../lib/api";
import {
  fetchPasskeys,
  isPasskeySupported,
  registerPasskey,
  type PasskeyItem,
} from "../lib/webauthn";

const DISMISS_KEY = "passkey-banner-dismissed";

function dismissedHeute(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === new Date().toISOString().slice(0, 10);
  } catch {
    return false;
  }
}

function defaultLabel(): string {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return "PC (Windows Hello)";
  if (/iPhone|iPad/i.test(ua)) return "iPhone";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac/i.test(ua)) return "Mac (Touch ID)";
  return "Dieses Gerät";
}

export default function PasskeyBanner() {
  const auth = useAuth();
  const qc = useQueryClient();
  const [label, setLabel] = useState(defaultLabel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(dismissedHeute);

  const supported = isPasskeySupported();
  const listQuery = useQuery<{ items: PasskeyItem[] }, Error>({
    queryKey: ["passkeys"],
    queryFn: fetchPasskeys,
    enabled: auth.isAuthenticated && supported,
    staleTime: 10 * 60_000,
    retry: (n: number, err: Error) => !(err instanceof UnauthorizedError) && n < 1,
  });

  if (
    hidden ||
    !supported ||
    !auth.isAuthenticated ||
    !listQuery.data ||
    listQuery.data.items.length > 0
  ) {
    return null;
  }

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10));
    } catch {
      // localStorage fehlt (Private Mode) — Banner bleibt Session-only weg.
    }
  };

  const register = async () => {
    setBusy(true);
    setError(null);
    try {
      await registerPasskey(label.trim() || defaultLabel());
      await qc.invalidateQueries({ queryKey: ["passkeys"] });
      // Liste hat jetzt 1 Eintrag → Banner verschwindet von selbst.
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Registrierung fehlgeschlagen";
      if (!/abort|abgebrochen|timed out|NotAllowed/i.test(msg)) {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-4 mt-3 sm:mx-6 rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span aria-hidden>🔑</span>
        <p className="min-w-0 flex-1 text-xs text-slate-200">
          Ein-Tap-Login aktivieren: Passkey für dieses Gerät einrichten —
          danach 30 Tage Session statt Magic-Link + Code.
        </p>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
          aria-label="Geräte-Name"
          className="w-40 rounded-lg border border-slate-700 bg-slate-950/60 px-2 py-1 text-xs text-slate-200 focus:border-sky-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={register}
          disabled={busy}
          className="rounded-lg bg-sky-500 px-3 py-1 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-50"
        >
          {busy ? "Warte auf Gerät …" : "Passkey einrichten"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Heute nicht mehr zeigen"
          className="px-1 text-slate-500 hover:text-slate-300"
        >
          ✕
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-rose-300 break-words">{error}</p>}
    </div>
  );
}
